import { useAuth } from '@/app/AuthContext.jsx';
import { extractList } from '@/lib/apiList';
import { qk } from '@/lib/queryKeys';
import { getCases, getTasks } from '@/lib/recepthor-api';
import { Search } from '@/ui/icons';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import './CommandPalette.css';

// Same floor as the case autocomplete in useCasesPick.
const SEARCH_MIN_CHARS = 2;
const SEARCH_DEBOUNCE_MS = 250;
const SEARCH_PAGE_SIZE = 5;

function useDebounced(value, delayMs) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(id);
  }, [value, delayMs]);
  return debounced;
}

export default function CommandPalette({
  open,
  onClose,
  groups,
  actions,
  onNavigate,
}) {
  const [query, setQuery] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const inputRef = useRef(null);
  const organizationId = useAuth()?.organizationId ?? null;

  const debouncedQuery = useDebounced(query, SEARCH_DEBOUNCE_MS);
  const searchTerm =
    debouncedQuery.trim().length >= SEARCH_MIN_CHARS
      ? debouncedQuery.trim()
      : '';

  const causasQuery = useQuery({
    queryKey: qk.commandPalette.causas(organizationId, searchTerm),
    queryFn: () =>
      getCases({
        page: 1,
        pageSize: SEARCH_PAGE_SIZE,
        search: searchTerm,
        organizationId,
      }),
    enabled: open && searchTerm !== '' && organizationId != null,
  });
  const diligenciasQuery = useQuery({
    queryKey: qk.commandPalette.diligencias(organizationId, searchTerm),
    queryFn: () =>
      getTasks({
        page: 1,
        pageSize: SEARCH_PAGE_SIZE,
        search: searchTerm,
        organizationId,
      }),
    enabled: open && searchTerm !== '' && organizationId != null,
  });

  // GET /cases returns `caseId`, not `id`.
  const causaItems = searchTerm
    ? extractList(causasQuery.data).map((c) => ({
        label: c.rol ?? c.caseId,
        path: `/causas/${c.caseId}`,
      }))
    : [];
  const diligenciaItems = searchTerm
    ? extractList(diligenciasQuery.data).map((t) => ({
        label: t.address || t.caseRol || t.id,
        path: `/diligencias/${t.id}`,
      }))
    : [];

  const isSearching =
    searchTerm !== '' && (causasQuery.isLoading || diligenciasQuery.isLoading);
  const searchFailed =
    searchTerm !== '' && (causasQuery.isError || diligenciasQuery.isError);

  const actionGroup = actions?.length
    ? [{ label: 'Acciones', items: actions }]
    : [];

  // Build a flat list of filtered items across all groups
  const filteredGroups = actionGroup
    .concat(groups || [])
    .map((group) => ({
      ...group,
      items: (group.items || []).filter((item) =>
        item.label.toLowerCase().includes(query.toLowerCase()),
      ),
    }))
    .filter((group) => group.items.length > 0)
    .concat(
      causaItems.length ? [{ label: 'Causas', items: causaItems }] : [],
      diligenciaItems.length
        ? [{ label: 'Diligencias', items: diligenciaItems }]
        : [],
    );

  const flatItems = filteredGroups.flatMap((g) => g.items);

  // Reset state when opening
  useEffect(() => {
    if (open) {
      setQuery('');
      setHighlightedIndex(0);
      // Defer focus to ensure the panel is mounted
      const id = setTimeout(() => {
        inputRef.current?.focus();
      }, 0);
      return () => clearTimeout(id);
    }
  }, [open]);

  // Reset highlight when query changes
  useEffect(() => {
    setHighlightedIndex(0);
  }, [query]);

  const navigate = useCallback(
    (path, state) => {
      if (state) onNavigate(path, { state });
      else onNavigate(path);
      onClose();
    },
    [onNavigate, onClose],
  );

  const handleKeyDown = useCallback(
    (e) => {
      if (!open) return;
      if (e.key === 'Escape') {
        // Swallow Escape so only the topmost layer closes. Listening in the
        // CAPTURE phase + stopPropagation prevents the event from reaching
        // document-level Escape listeners underneath (e.g. an open Modal),
        // so the palette closes without also closing the layer behind it.
        e.preventDefault();
        e.stopPropagation();
        onClose();
        return;
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setHighlightedIndex((i) => Math.min(i + 1, flatItems.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setHighlightedIndex((i) => Math.max(i - 1, 0));
      } else if (e.key === 'Enter') {
        const item = flatItems[highlightedIndex] ?? flatItems[0];
        if (item) navigate(item.path, item.state);
      }
    },
    [open, onClose, flatItems, highlightedIndex, navigate],
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown, true);
    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, [handleKeyDown]);

  if (!open) return null;

  let flatIdx = 0;

  return (
    <div className="cmd-palette__scrim" onClick={onClose}>
      <div
        className="cmd-palette__panel"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Buscar causas, diligencias y acciones"
      >
        <div className="cmd-palette__input-row">
          {/* Shared Search glyph; stroke 2.25 on the 24 grid ≡ the old 1.5 on
              this icon's original 16 grid at 16px. */}
          <Search
            className="cmd-palette__search-icon"
            size={16}
            strokeWidth={2.25}
          />
          <input
            ref={inputRef}
            className="cmd-palette__input"
            type="text"
            placeholder="Buscar..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label="Buscar causas, diligencias y acciones"
            aria-autocomplete="list"
          />
          <span className="cmd-palette__trigger-hint">⌘K</span>
        </div>

        {/* A failed search sits above the local items rather than replacing
            them, so the sidebar items and actions stay reachable. */}
        {searchFailed && (
          <div className="cmd-palette__empty" role="alert">
            No se pudo buscar. Intenta nuevamente.
          </div>
        )}
        {flatItems.length === 0 ? (
          !searchFailed && (
            <div className="cmd-palette__empty" aria-live="polite">
              {isSearching ? 'Buscando…' : 'Sin resultados'}
            </div>
          )
        ) : (
          <ul className="cmd-palette__list" role="listbox">
            {filteredGroups.map((group) => (
              <li key={group.label} className="cmd-palette__group">
                <span className="cmd-palette__group-label">{group.label}</span>
                <ul role="group" aria-label={group.label}>
                  {group.items.map((item) => {
                    const idx = flatIdx++;
                    const isHighlighted = idx === highlightedIndex;
                    const hasIcon = typeof item.Icon === 'function';
                    return (
                      <li
                        key={item.path}
                        className={`cmd-palette__item${isHighlighted ? ' cmd-palette__item--highlighted' : ''}`}
                        role="option"
                        aria-selected={isHighlighted}
                        onClick={() => navigate(item.path, item.state)}
                        onMouseEnter={() => setHighlightedIndex(idx)}
                      >
                        <span className="cmd-palette__item-left">
                          {hasIcon && (
                            <span
                              className="cmd-palette__item-icon"
                              aria-hidden="true"
                            >
                              <item.Icon />
                            </span>
                          )}
                          <span className="cmd-palette__item-label">
                            {item.label}
                          </span>
                        </span>
                        {item.shortcut && (
                          <span
                            className="cmd-palette__item-shortcut"
                            aria-label={`Atajo G ${item.shortcut}`}
                          >
                            <kbd>G</kbd>
                            <kbd>{item.shortcut}</kbd>
                          </span>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
